import React from 'react';
import {
  Input, Button, Space, Card, InputNumber, DatePicker,
  Alert, Empty, message as antMessage, Row, Col, Divider,
} from 'antd';
import { PrinterOutlined, EyeOutlined, ClearOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { usePWJ30BPrint } from './hooks/usePWJ30BPrint';
import './PWJ30BForm.css';

const { TextArea } = Input;

const PWJ30BForm: React.FC = () => {
  const {
    formValues,
    previewResult,
    loading,
    previewed,
    printStatus,
    handleFieldChange,
    handlePreview,
    handlePrint,
    handleReset,
  } = usePWJ30BPrint();

  const handleWindowPrint = () => {
    if (!previewResult || previewResult.total_count === 0) {
      antMessage.warning('請先預覽標籤資料');
      return;
    }
    window.print();
  };

  return (
    <PermissionGuard module={Modules.PWJ30B} action="query">
      <div className="pwj30b-container">
        <div className="pwj30b-header">
          <h2>標簽貼紙列印</h2>
          <Space>
            <Button icon={<EyeOutlined />} onClick={handlePreview} loading={loading}>
              預覽
            </Button>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handlePrint}
              loading={loading}
            >
              開始列印
            </Button>
            {previewed && previewResult && previewResult.total_count > 0 && (
              <Button icon={<PrinterOutlined />} onClick={handleWindowPrint}>
                瀏覽器列印
              </Button>
            )}
            <Button icon={<ClearOutlined />} onClick={handleReset}>
              清除
            </Button>
          </Space>
        </div>

        <Card size="small" className="pwj30b-form-card">
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={12} md={8}>
              <div className="pwj30b-field">
                <label>Invoice no.</label>
                <Input
                  value={formValues.invoice_no}
                  onChange={e => handleFieldChange('invoice_no', e.target.value)}
                  maxLength={10}
                  placeholder="XXXXXXXXXX"
                  allowClear
                />
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="pwj30b-field">
                <label>Order no.</label>
                <Input
                  value={formValues.order_no}
                  onChange={e => handleFieldChange('order_no', e.target.value)}
                  maxLength={10}
                  placeholder="XXXXXXXXXX"
                  allowClear
                />
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="pwj30b-field">
                <label>Article 品號</label>
                <Input
                  value={formValues.article}
                  onChange={e => handleFieldChange('article', e.target.value)}
                  maxLength={13}
                  placeholder="XXXXXXXXXXXXX"
                  allowClear
                />
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="pwj30b-field">
                <label>Color 顏色代號</label>
                <Input
                  value={formValues.color}
                  onChange={e => handleFieldChange('color', e.target.value)}
                  maxLength={15}
                  placeholder="XXXXXXXXXXXXXXX"
                  allowClear
                />
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="pwj30b-field">
                <label>Qty in M 公制數量</label>
                <Input
                  value={formValues.qty_m}
                  onChange={e => handleFieldChange('qty_m', e.target.value)}
                  maxLength={5}
                  placeholder="XXXXX"
                  allowClear
                />
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="pwj30b-field">
                <label>Qty in yd 碼制數量</label>
                <Input
                  value={formValues.qty_yd}
                  onChange={e => handleFieldChange('qty_yd', e.target.value)}
                  maxLength={5}
                  placeholder="XXXXX"
                  allowClear
                />
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="pwj30b-field">
                <label>Grade 等級</label>
                <Input
                  value={formValues.grade}
                  onChange={e => handleFieldChange('grade', e.target.value)}
                  maxLength={6}
                  placeholder="XXXXXX"
                  allowClear
                />
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="pwj30b-field">
                <label>Date 日期</label>
                <DatePicker
                  value={formValues.label_date}
                  onChange={v => handleFieldChange('label_date', v)}
                  format="YYYY/MM/DD"
                  style={{ width: '100%' }}
                />
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="pwj30b-field">
                <label>Insp 檢驗員</label>
                <Input
                  value={formValues.inspector}
                  onChange={e => handleFieldChange('inspector', e.target.value)}
                  maxLength={6}
                  placeholder="XXXXXX"
                  allowClear
                />
              </div>
            </Col>
            <Col xs={24} sm={24} md={16}>
              <div className="pwj30b-field">
                <label>Finish 後整理說明</label>
                <TextArea
                  value={formValues.finish}
                  onChange={e => handleFieldChange('finish', e.target.value)}
                  maxLength={50}
                  rows={2}
                  placeholder="後整理/加工處理說明（最多50字）"
                />
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="pwj30b-field">
                <label>列印份數</label>
                <InputNumber
                  value={formValues.print_copies}
                  onChange={v => handleFieldChange('print_copies', v ?? 1)}
                  min={1}
                  max={99}
                  style={{ width: '100%' }}
                />
              </div>
            </Col>
          </Row>
        </Card>

        {printStatus && (
          <Alert
            message="列印狀態"
            description={<pre className="pwj30b-status-pre">{printStatus}</pre>}
            type="info"
            style={{ marginTop: 16 }}
          />
        )}

        {previewed && previewResult && previewResult.total_count === 0 && (
          <Empty description="查無此資料" style={{ marginTop: 40 }} />
        )}

        {previewed && previewResult && previewResult.total_count > 0 && (
          <div className="pwj30b-preview" id="pwj30b-print-area">
            <Divider>標籤預覽（共 {previewResult.total_count} 份）</Divider>
            <div className="pwj30b-label-grid">
              {previewResult.records.slice(0, 5).map((rec, idx) => (
                <div key={idx} className="pwj30b-label-card">
                  <div className="pwj30b-label-row">
                    <span className="pwj30b-label-key">Invoice No.</span>
                    <span className="pwj30b-label-val">{rec.invoice_no}</span>
                  </div>
                  <div className="pwj30b-label-row">
                    <span className="pwj30b-label-key">Order No.</span>
                    <span className="pwj30b-label-val">{rec.order_no}</span>
                  </div>
                  <div className="pwj30b-label-row">
                    <span className="pwj30b-label-key">Article</span>
                    <span className="pwj30b-label-val">{rec.article}</span>
                  </div>
                  <div className="pwj30b-label-row">
                    <span className="pwj30b-label-key">Color</span>
                    <span className="pwj30b-label-val">{rec.color}</span>
                  </div>
                  <div className="pwj30b-label-row">
                    <span className="pwj30b-label-key">Finish</span>
                    <span className="pwj30b-label-val">{rec.finish}</span>
                  </div>
                  <div className="pwj30b-label-row">
                    <span className="pwj30b-label-key">Qty M / yd</span>
                    <span className="pwj30b-label-val">{rec.qty_m} / {rec.qty_yd}</span>
                  </div>
                  <div className="pwj30b-label-row">
                    <span className="pwj30b-label-key">Grade</span>
                    <span className="pwj30b-label-val">{rec.grade}</span>
                  </div>
                  <div className="pwj30b-label-row">
                    <span className="pwj30b-label-key">Date</span>
                    <span className="pwj30b-label-val">
                      {rec.label_date ? dayjs(rec.label_date).format('YYYY/MM/DD') : ''}
                    </span>
                  </div>
                  <div className="pwj30b-label-row">
                    <span className="pwj30b-label-key">Insp</span>
                    <span className="pwj30b-label-val">{rec.inspector}</span>
                  </div>
                  {idx === 0 && previewResult.total_count > 1 && (
                    <div className="pwj30b-label-copy-note">
                      (顯示第 1 份，共 {previewResult.total_count} 份)
                    </div>
                  )}
                </div>
              ))}
              {previewResult.total_count > 5 && (
                <div className="pwj30b-label-more">
                  ...還有 {previewResult.total_count - 5} 份（共 {previewResult.total_count} 份）
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </PermissionGuard>
  );
};

export default PWJ30BForm;
